/* SessionsSheet — bottom-sheet that lists every device currently logged into
   this LexIQ account. Lets the user revoke any device individually, or hit
   "تَسجيل الخُروج من البَقيّة" to nuke every other session in one tap.

   This is the user-facing half of the server-side device-limit enforcement
   in services/sessions.py — it gives the legitimate lawyer transparency
   and control when their plan cap kicks in. */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  Devices, Trash, Spinner, Check, CaretRight, Warning,
} from "@phosphor-icons/react";
import {
  listMySessions, revokeMySession, signOutOtherSessions,
} from "../../lib/api";

const CARD = "bg-white/85 backdrop-blur-md rounded-2xl border border-white/60";

function fmt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "الآن";
    if (diff < 3600) return `قَبل ${Math.floor(diff / 60)} د`;
    if (diff < 86400) return `قَبل ${Math.floor(diff / 3600)} س`;
    return `قَبل ${Math.floor(diff / 86400)} يوم`;
  } catch {
    return "";
  }
}

export default function SessionsSheet({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [maxDevices, setMaxDevices] = useState(1);
  const [revoking, setRevoking] = useState(null);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await listMySessions();
      setSessions(data.sessions || []);
      setMaxDevices(data.max_devices || 1);
    } catch {
      toast.error("تَعَذَّر جَلب الجَلَسات");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const onRevoke = async (sid) => {
    setRevoking(sid);
    try {
      await revokeMySession(sid);
      toast.success("تَمَّ إنهاء الجَلسَة");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تَعَذَّر إنهاء الجَلسَة");
    } finally {
      setRevoking(null);
    }
  };

  const onSignOutOthers = async () => {
    if (!window.confirm("سَيَتِمّ تَسجيل خُروج كُلّ أَجهِزَتِك الأُخرى. هل أَنت مُتَأَكِّد؟")) return;
    setSigningOut(true);
    try {
      const r = await signOutOtherSessions();
      toast.success(`تَمَّ تَسجيل خُروج ${r.revoked || 0} جِهاز`);
      await reload();
    } catch {
      toast.error("تَعَذَّر تَنفيذ العَمَليّة");
    } finally {
      setSigningOut(false);
    }
  };

  const otherCount = sessions.filter((s) => !s.is_current).length;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="sessions-sheet-backdrop"
    >
      <div
        className={`${CARD} w-full sm:max-w-md max-h-[88vh] overflow-y-auto m-0 sm:m-4 rounded-b-none sm:rounded-2xl`}
        onClick={(e) => e.stopPropagation()}
        data-testid="sessions-sheet"
      >
        <div className="p-5 border-b border-[#E5E7EB] flex items-center gap-3">
          <Devices size={22} weight="duotone" className="text-[#D4AF37]" />
          <div className="flex-1">
            <div className="text-[15px] font-bold text-[#0A192F]">الأَجهِزَة النَّشِطَة</div>
            <div className="text-[11.5px] text-[#6B7280]">
              {sessions.length} / {maxDevices === 9999 ? "♾️" : maxDevices} جِهاز
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[13px] text-[#6B7280]"
            data-testid="sessions-sheet-close"
          >
            إغلاق
          </button>
        </div>

        {maxDevices !== 9999 && (
          <div className="mx-5 mt-4 p-3 bg-[#FFF7E6] border border-[#F2C94C]/40 rounded-xl flex items-start gap-2">
            <Warning size={16} weight="fill" className="text-[#B45309] flex-shrink-0 mt-0.5" />
            <div className="text-[12px] text-[#92400E] leading-relaxed">
              باقَتُك تَسمَح بِـ <b>{maxDevices}</b> جِهاز فَقَط. إذا سَجَّلت دُخولك من جِهاز إضافي،
              سَيَتِمّ تَسجيل خُروج أَقدَم جِهاز تِلقائيّاً.
            </div>
          </div>
        )}

        <div className="p-5 space-y-2">
          {loading ? (
            <div className="flex justify-center py-6">
              <Spinner size={24} weight="bold" className="animate-spin text-[#D4AF37]" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center text-[13px] text-[#6B7280] py-6">
              لا تُوجَد جَلَسات نَشِطَة.
            </div>
          ) : (
            sessions.map((s) => (
              <div
                key={s.id}
                className="flex items-center gap-3 p-3 rounded-xl bg-white border border-[#E5E7EB]"
                data-testid={`session-row-${s.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-semibold text-[#0A192F] flex items-center gap-2">
                    {s.device_label}
                    {s.is_current && (
                      <span className="inline-flex items-center gap-1 text-[10.5px] bg-[#10B981]/15 text-[#047857] px-2 py-0.5 rounded-full">
                        <Check size={10} weight="bold" /> هذا الجِهاز
                      </span>
                    )}
                    {s.auth_method === "webauthn" && (
                      <span className="text-[10.5px] bg-[#D4AF37]/15 text-[#92400E] px-2 py-0.5 rounded-full">
                        بَصمَة
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#6B7280] mt-0.5 truncate">
                    {s.ip || "—"} · آخر نَشاط: {fmt(s.last_seen_at)}
                  </div>
                </div>
                {!s.is_current && (
                  <button
                    onClick={() => onRevoke(s.id)}
                    disabled={revoking === s.id}
                    className="w-9 h-9 rounded-lg bg-[#FEE2E2] flex items-center justify-center active:scale-95 transition-transform disabled:opacity-50"
                    data-testid={`session-revoke-${s.id}`}
                    aria-label="إنهاء الجَلسَة"
                  >
                    {revoking === s.id
                      ? <Spinner size={14} weight="bold" className="animate-spin text-[#B91C1C]" />
                      : <Trash size={14} weight="bold" className="text-[#B91C1C]" />
                    }
                  </button>
                )}
              </div>
            ))
          )}
        </div>

        {otherCount > 0 && (
          <div className="px-5 pb-5">
            <button
              onClick={onSignOutOthers}
              disabled={signingOut}
              className="w-full py-3 rounded-xl bg-[#0A192F] text-white text-[13px] font-semibold active:scale-[0.98] transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="sessions-signout-others-btn"
            >
              {signingOut ? <Spinner size={14} weight="bold" className="animate-spin" /> : null}
              تَسجيل خُروج {otherCount} جِهاز آخَر
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
